import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Dropdown,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';

import { getMigrations } from '../api/migrations';
import IdentityWorkspace from '../components/identity/IdentityWorkspace';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';
import { EmptyState, HeroBand } from '../components/abPrimitives';
import { TEXT } from '../theme/tokens';

// Resources › Identities — the FD-ADR-037 §1 identity workspace, mounted here
// and on the campaign Identities tab. ONE editing implementation, two entry
// points; nothing here re-implements staging, the freeze rules, or the
// T2T-GUID/D2C-SID key normalisation.
//
// FD-512 (R-FD-053, owner decision D4) — A CAMPAIGN IS REQUIRED. The FD-284
// cross-campaign inventory that used to be this page's default view is
// retired: with no `?migrationId=`, the page says a campaign is required and
// offers the selector rather than rendering a table.
//
// WHAT THAT COSTS, recorded rather than discovered. "Which campaign is this
// user in?" now has no cross-campaign answer in the console. That was the
// inventory's one genuinely unique capability, and D4 traded it for a page
// that does one thing rather than two unrelated things. The endpoint behind it
// — `GET /v1/identity-mappings` (`manager/get-identity-mappings-all`) — STAYS
// DEPLOYED, and `api/identityMappings.ts` stays in the tree with the same note,
// so re-consuming it is a frontend-only change and not a backend one.
//
// Two properties survive the retirement unchanged, and both are load-bearing:
//   • Suggestions stay strictly PER-CAMPAIGN. Selecting a campaign fetches THAT
//     campaign's suggestions; nothing here fans Graph work across campaigns
//     (FD-221; FD-ADR-030 §4). The selector is what makes that structural —
//     there is no longer any code path on this page that reads more than one
//     campaign's identities.
//   • The campaign list is still one page of campaigns used only to turn ids
//     into NAMES. A raw GUID is the exact complaint FD-ADR-030 raised against
//     `Tenants.LinkedMigrations`.

// One page of campaigns, used only to turn a `migrationId` into a NAME. A raw
// GUID column is the exact complaint FD-ADR-030 raised against
// `Tenants.LinkedMigrations`. Beyond this bound a campaign shows its id — the
// same visible-rather-than-silent posture as an unresolvable group.
const CAMPAIGN_NAME_PAGE = 100;

export default function IdentitiesListPage() {
  const campaignsQuery = useQuery({
    queryKey: ['migrations', { pageSize: CAMPAIGN_NAME_PAGE }],
    queryFn: () => getMigrations({ pageSize: CAMPAIGN_NAME_PAGE }),
  });

  const campaignNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of campaignsQuery.data?.migrations ?? []) {
      map.set(m.migrationId, m.migrationName);
    }
    return map;
  }, [campaignsQuery.data]);

  // The selected campaign lives in the URL (`?migrationId=`), never
  // localStorage (ADR-015's surviving rule), so a workspace view is linkable
  // and survives reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMigrationId = searchParams.get('migrationId');

  // Same hook, same ['migration', id] cache key and staleTime the campaign tab
  // uses, so opening a campaign here and then navigating into it does not
  // re-read the DeviceMigrations partition. Disabled when nothing is selected.
  const detailQuery = useMigrationDetailQuery(selectedMigrationId ?? undefined);

  const selectCampaign = (next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('migrationId', next);
    else params.delete('migrationId');
    setSearchParams(params, { replace: true });
  };

  const selectedCampaignName = selectedMigrationId
    ? campaignNames.get(selectedMigrationId) ?? selectedMigrationId
    : null;

  const campaigns = campaignsQuery.data?.migrations ?? [];

  return (
    <div className="flex flex-col gap-6">
      <HeroBand
        eyebrow="Resources"
        title="Identities"
        subtitle="Review a campaign's identity suggestions and edit its mappings. While a campaign's wave is active you can still add a mapping, but not change or remove one."
      />

      {/* The campaign selector. Suggestions are per-campaign by construction —
          they cost Graph calls per unique user — so this SELECTS one and never
          aggregates. There is no "All campaigns" option, because there is no
          longer anything for it to render. */}
      <div className="flex flex-wrap items-center gap-3">
        <label
          htmlFor="identity-campaign"
          className="font-display text-sm"
          style={{ color: TEXT.secondary }}
        >
          Campaign
        </label>
        <Dropdown
          id="identity-campaign"
          aria-label="Campaign"
          placeholder="Select a campaign"
          value={selectedCampaignName ?? ''}
          selectedOptions={selectedMigrationId ? [selectedMigrationId] : []}
          onOptionSelect={(_, data) => selectCampaign(data.optionValue || null)}
          disabled={campaignsQuery.isLoading}
          style={{ minWidth: '260px' }}
        >
          {campaigns.map((m) => (
            <Option key={m.migrationId} value={m.migrationId} text={m.migrationName}>
              {m.migrationName}
            </Option>
          ))}
        </Dropdown>
      </div>

      {selectedMigrationId ? (
        <CampaignWorkspacePanel
          migrationId={selectedMigrationId}
          detailQuery={detailQuery}
        />
      ) : (
        <NoCampaignSelected
          isLoading={campaignsQuery.isLoading}
          isError={campaignsQuery.isError}
          hasCampaigns={campaigns.length > 0}
          onRetry={() => campaignsQuery.refetch()}
        />
      )}
    </div>
  );
}

// FD-512 — the landing state. It states the requirement rather than rendering
// an empty table: a table with no rows and no explanation reads as "there are
// no identity mappings in this deployment", which is a claim this page can no
// longer make and could never verify.
function NoCampaignSelected({
  isLoading,
  isError,
  hasCampaigns,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  hasCampaigns: boolean;
  onRetry: () => void;
}) {
  if (isLoading) {
    return (
      <div className="font-display text-sm" style={{ color: TEXT.secondary }}>
        Loading campaigns…
      </div>
    );
  }

  if (isError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Could not load campaigns</MessageBarTitle>
          Identity mappings are reviewed per campaign, so the list is needed before one can be
          chosen.
        </MessageBarBody>
        <MessageBarActions>
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  }

  if (!hasCampaigns) {
    return (
      <EmptyState
        title="No campaigns yet"
        detail="Identity mappings are supplied per campaign, before its wave is activated. Create a campaign from the New menu to begin."
      />
    );
  }

  // Deliberately NOT "Select a campaign": that is the dropdown's own
  // placeholder, and a panel restating the control beside it is the
  // restatement FD-504 removed from the check-in cell. This names the STATE;
  // the control says what to do about it.
  return (
    <EmptyState
      title="No campaign selected"
      detail="Identity suggestions are computed per campaign against its own source and destination tenants, so this page works one campaign at a time. Choose one above to review its suggestions and edit its mappings."
    />
  );
}

// FD-380 — the campaign branch of Resources › Identities.
//
// Extracted so the page's hook order is fixed regardless of which branch
// renders (the FD-188 trap ADR-031 cites): every hook above runs on both
// paths, and this component owns only its own render.
function CampaignWorkspacePanel({
  migrationId,
  detailQuery,
}: {
  migrationId: string;
  detailQuery: ReturnType<typeof useMigrationDetailQuery>;
}) {
  if (detailQuery.isLoading) {
    return (
      <div className="font-display text-sm" style={{ color: TEXT.secondary }}>
        Loading campaign…
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Could not load this campaign</MessageBarTitle>
          {detailQuery.error instanceof Error
            ? detailQuery.error.message
            : 'The campaign may have been removed.'}
        </MessageBarBody>
        <MessageBarActions>
          <Button size="small" onClick={() => detailQuery.refetch()}>
            Retry
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  }

  const { migration, devices } = detailQuery.data;
  const migrationType = migration.migrationType;

  // Same guard the campaign tab applies — identity reconciliation is a T2T/D2C
  // concept, and an unrecognised type must read as "not applicable" rather than
  // render an editing surface over a contract that has none.
  if (migrationType !== 'T2T' && migrationType !== 'D2C') {
    return (
      <div className="max-w-xl">
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>No identity mappings for this migration type</MessageBarTitle>
            Identity reconciliation applies to tenant-to-tenant and domain-to-cloud migrations
            only.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <IdentityWorkspace
      migrationId={migrationId}
      migrationType={migrationType}
      bprtStatus={migration.bprtStatus}
      deviceCount={devices.length}
      sourceTenantDomain={migration.sourceTenantDomain}
      sourceTenantId={migration.sourceTenantId}
      destTenantDomain={migration.destTenantDomain}
      destTenantId={migration.destTenantId}
    />
  );
}
